import { computed } from "vue";
import { useI18n } from "vue-i18n";
import MoeLessonVoteApi from "@apis/moe.lesson.vote.api.ts";
import LessonVoteApi from "@apis/lesson.vote.api.ts";
import { useErrorHandler } from "@plugins/common/composables/error.ts";
import { useModuleUpvoteStore } from "./useModuleUpvoteStore.ts";

/**
 * Options for the useModuleUpvote composable.
 *
 * Plain values are intentional — lessonUuid and isMoeLesson are fixed
 * for the entire lifetime of any component that calls this composable.
 */
interface UseModuleUpvoteOptions {
  /** UUID of the lesson to upvote. */
  lessonUuid: string;
  /** true = MOE API, false = CG (Community Gallery) API. */
  isMoeLesson: boolean;
}

/**
 * Composable that encapsulates upvote state and API calls for a lesson module.
 *
 * Supports both MOE Library and Community Gallery domains by switching the
 * underlying API based on the `isMoeLesson` flag.
 *
 * @param options - Configuration options containing lessonUuid and isMoeLesson.
 * @returns Reactive state and methods for managing the upvote.
 */
export function useModuleUpvote(options: UseModuleUpvoteOptions) {
  const { lessonUuid, isMoeLesson } = options;
  const { t } = useI18n();
  const { passiveApiFailHandler } = useErrorHandler();

  /** Selects the correct vote API based on the lesson domain — fixed at call time. */
  const api = isMoeLesson ? MoeLessonVoteApi : LessonVoteApi;

  /**
   * Shared, store-backed state — keyed per lesson(+domain) so every consumer
   * mounted for the same lesson (header, wait-modal prompt, end page, ...)
   * reads/writes the same Ref, keeping their displayed state in sync.
   */
  const voteStore = useModuleUpvoteStore();
  const entry = voteStore.getEntry(lessonUuid, isMoeLesson);

  /** Whether the current user has already upvoted this lesson. */
  const hasVoted = entry.hasVoted;

  /** True while an API call (create or delete vote) is in flight. */
  const isLoading = entry.isLoading;

  /**
   * Toggles the upvote state: creates a vote when not voted, deletes it when voted.
   *
   * Errors from the API are NOT silently discarded — they are rethrown so that
   * the caller can handle or display them appropriately.
   *
   * @throws {Error} When the API returns a non-ok response.
   */
  async function toggleUpvote(): Promise<void> {
    if (isLoading.value) return;
    isLoading.value = true;
    try {
      const response = hasVoted.value
        ? await api.deleteLessonVote(lessonUuid)
        : await api.createLessonVote(lessonUuid);

      if (response.code !== "ok") {
        const message = Object.values(response.errorMap)[0] ?? "Upvote operation failed";
        throw new Error(message);
      }

      hasVoted.value = !hasVoted.value;
    } finally {
      isLoading.value = false;
    }
  }

  /**
   * Icon name for the upvote button — filled when voted, unfilled when not.
   */
  const upvoteIconName = computed<string>(() =>
    hasVoted.value ? "UpvoteFilled24" : "UpvoteUnfilled24",
  );

  /**
   * Accessible label for the upvote button.
   */
  const upvoteLabel = computed<string>(() =>
    hasVoted.value ? t("button_remove_up_vote") : t("button_upvote"),
  );

  /**
   * Fetches the current vote state from the API and seeds the shared state.
   * Should be called on mount when the upvote prompt is visible.
   * Failures surface via passiveApiFailHandler (same pattern as the
   * StarResourceButton/LessonSearchCard precedents this composable
   * generalizes) — upvote state defaults to false, but the user still sees
   * the error banner.
   *
   * Deduped per lesson via entry.fetchPromise: concurrent/later callers for
   * the same lesson await the same in-flight/completed request instead of
   * re-hitting the API. Only a *successful* fetch is cached — a failed
   * attempt clears entry.fetchPromise so a later call retries instead of
   * being permanently stuck on the failure.
   */
  async function fetchUpvoteState(): Promise<void> {
    if (!lessonUuid) return;
    if (!entry.fetchPromise) {
      entry.fetchPromise = (async () => {
        try {
          const response = await api.checkHasLessonVote(lessonUuid);
          if (response.code === "ok") {
            hasVoted.value = response.data;
          } else {
            entry.fetchPromise = null;
            passiveApiFailHandler(response);
          }
        } catch (err) {
          // Don't cache the failure — allow a later call to retry.
          entry.fetchPromise = null;
          passiveApiFailHandler(err);
        }
      })();
    }
    await entry.fetchPromise;
  }

  return {
    hasVoted,
    isLoading,
    fetchUpvoteState,
    toggleUpvote,
    upvoteIconName,
    upvoteLabel,
  };
}
